import { clsx } from 'clsx';
import { useState } from 'react';
import { Content } from 'src/contexts/ContentContext';
import { vars } from 'src/core/themes';
import { DropdownMenu } from 'src/core/ui';
import { PinFilledIcon, PinIcon } from 'src/svgs';
import { ChevronDownIcon, ChevronUpIcon, IconsFill } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';

import * as styles from './PageSizeSelector.css';

export const PAGE_SIZE_OPTIONS = [100, 200, 500, 1000] as const;
export type PageSizeOption = (typeof PAGE_SIZE_OPTIONS)[number];

export const DEFAULT_SEARCH_PAGE_SIZE: PageSizeOption = 1000;
export const MAX_SEARCH_PAGE_SIZE: PageSizeOption = 1000;

/**
 * Clamps a page size value to the nearest valid option.
 * If the value is not one of the valid options, it falls back to the default (100).
 */
export function clampPageSize(
  value: number | null | undefined
): PageSizeOption {
  if (value != null && PAGE_SIZE_OPTIONS.includes(value as PageSizeOption)) {
    return value as PageSizeOption;
  }
  return DEFAULT_SEARCH_PAGE_SIZE;
}

type PageSizeSelectorProps = {
  /**
   * The currently active (session) page size. This is displayed in the trigger
   * and used for the current view. It may differ from pinnedPageSize when the
   * user has temporarily switched page sizes without pinning.
   */
  value: PageSizeOption;
  /**
   * The persisted (pinned) default page size. The pin icon is filled/active on
   * this option.
   */
  pinnedPageSize?: PageSizeOption;
  /**
   * Called when a menu item is selected. Changes the session page size.
   */
  onChange: (newSize: PageSizeOption) => void;
  /**
   * Called when the user clicks the pin icon on a menu item. Persists the chosen
   * size as the new default and also applies it to the current session.
   */
  onPinPageSize?: (newSize: PageSizeOption) => void;
  disabled?: boolean;
};

/**
 * A dropdown selector for choosing the number of results per page in Advanced Search V2.
 * Options: 100, 200, 500, 1000.
 *
 * Supports two interaction modes:
 * - Clicking a menu item switches the page size for the current session.
 * - Clicking the pin icon switches the page size AND persists it as the new default.
 *
 * The dropdown opens upward (side="top") since it's placed at the bottom of the page.
 */
export const PageSizeSelector = ({
  value,
  pinnedPageSize,
  onChange,
  onPinPageSize,
  disabled,
}: PageSizeSelectorProps) => {
  const effectivePinned = pinnedPageSize ?? value;
  const [open, setOpen] = useState(false);

  return (
    <DropdownMenu.Root open={open} onOpenChange={setOpen}>
      <DropdownMenu.Trigger asChild disabled={disabled}>
        <button className={styles.trigger} aria-label="Results per page">
          <span className={styles.triggerLabel}>
            <Content id={ContentId.ResultsPerPage} />:
          </span>
          <span className={styles.triggerValue}>{value}</span>
          {/* Chevron affordance area: 32×16px click target */}
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '32px',
              height: '16px',
              flexShrink: 0,
            }}
          >
            {open ? (
              <ChevronUpIcon
                size={vars.iconSize.xs}
                fill={IconsFill.currentColor}
              />
            ) : (
              <ChevronDownIcon
                size={vars.iconSize.xs}
                fill={IconsFill.currentColor}
              />
            )}
          </span>
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content
        side="top"
        align="end"
        style={{ minWidth: 'var(--radix-dropdown-menu-trigger-width)' }}
      >
        {PAGE_SIZE_OPTIONS.map((size) => (
          <DropdownMenu.Item
            key={size}
            onSelect={() => {
              onChange(size);
            }}
          >
            <div className={styles.optionRow}>
              <span className={styles.optionLabel}>{size}</span>
              {size === effectivePinned && (
                <span className={styles.defaultLabel}>
                  <Content id={ContentId.Default} />
                </span>
              )}
              <button
                data-pin-button
                className={clsx(styles.pinButton, {
                  [styles.pinButtonActive]: size === effectivePinned,
                  [styles.pinButtonHidden]: size !== effectivePinned,
                })}
                aria-label={
                  size === effectivePinned
                    ? `${size} is pinned as default`
                    : `Pin ${size} as default`
                }
                onClick={() => {
                  if (onPinPageSize && size !== effectivePinned) {
                    onPinPageSize(size);
                  }
                }}
                tabIndex={-1}
              >
                {size === effectivePinned ? (
                  <PinIcon
                    size={vars.iconSize.m}
                    title={`${size} is pinned as default`}
                  />
                ) : (
                  <PinFilledIcon
                    size={vars.iconSize.m}
                    title={`Pin ${size} as default`}
                  />
                )}
              </button>
            </div>
          </DropdownMenu.Item>
        ))}
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
};
